import { useEffect, useRef } from 'react';
import {
  CircularProgress,
  DrawerProps,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Tooltip
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import MDBox from '@components/MDBox';
import { useMaterialUIController } from '@context/MDContext';
import {
  CLICKED_DS_RECOMMENDATION,
  CLICKED_FILTER,
  CLICKED_SETTINGS,
  CLICKED_THEME
} from '@constants/eventNames';
import { sendAmplitudeData } from '@utils/amplitude';
import ConfiguratorRoot from './ConfiguratorRoot';
import FaIcon from '@components/FaIcon';

import './styles.scss';

interface Props extends DrawerProps {
  children?: React.ReactNode;
  marginTop?: string;
  width?: number | string;
  configuratorFlag: number | string;
  configuratorTitle?: string;
  setConfiguratorFlag?: any;
  isEditChart?: boolean;
  isProjectBoard?: boolean;
  onCloseConfigurator: () => void;
  isLoading?: boolean;
  hideRecommendations?: boolean;
  paddingBottom?: string;
}

function Configurator({
  children,
  marginTop = '0px',
  width,
  configuratorFlag,
  configuratorTitle,
  setConfiguratorFlag,
  isEditChart = false,
  isProjectBoard = false,
  onCloseConfigurator,
  isLoading,
  hideRecommendations = false,
  paddingBottom,
  ...rest
}: Props): JSX.Element {
  const [controller] = useMaterialUIController();
  const { openConfigurator, preventClosing } = controller;

  const ref = useRef() as React.MutableRefObject<HTMLDivElement>;

  const tabsRef = useRef<HTMLButtonElement>(null);

  const handleCloseConfigurator = (e: any) => {
    if (!e.target?.offsetParent?.classList.contains('preventBgClose'))
      onCloseConfigurator();
  };

  const getCloseMessage = (configuratorFlag?: string | number): String => {
    switch (configuratorFlag) {
      case 1:
        return 'Click to close Comments section';
      case 'comments':
        return 'Click to close Comments section';
      case 'settings':
        return 'Click to close View settings';
      case 'theme':
        return 'Click to close Board Settings';
      default:
        return 'Click to close';
    }
  };

  const editChartTabs = ['Settings', 'Theme', 'Filter', 'Comments'];

  if (!hideRecommendations) editChartTabs.push('Recommendations');

  const projectBoardTabs = isProjectBoard
    ? ['Theme', 'Filter', 'Comments']
    : ['Theme', 'Comments'];

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setConfiguratorFlag(newValue);
  };

  const handleEditChartTabsClick = (tab: string) =>
    ({
      Settings: () => sendAmplitudeData(CLICKED_SETTINGS),
      Theme: () => sendAmplitudeData(CLICKED_THEME),
      Filter: () => sendAmplitudeData(CLICKED_FILTER),
      Recommendations: () => sendAmplitudeData(CLICKED_DS_RECOMMENDATION)
    }[tab]);

  const handleProjectBoardTabsClick = (tab: string) =>
    ({
      Theme: () => sendAmplitudeData(CLICKED_THEME),
      Filter: () => sendAmplitudeData(CLICKED_FILTER)
    }[tab]);

  useEffect(() => {
    // Scroll the selected tab into view when changed from outside click
    if (tabsRef.current && openConfigurator && !isLoading) {
      const selectedTab = tabsRef.current.querySelector('.Mui-selected');

      if (selectedTab)
        setTimeout(() => {
          selectedTab.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest'
          });
        }, 500);
    }
  }, [openConfigurator, configuratorFlag, isLoading]);

  return (
    <ConfiguratorRoot
      {...rest}
      variant='permanent'
      ownerState={{ openConfigurator, marginTop, width, paddingBottom }}
      ref={ref}
      className='configurator-cntr'
    >
      {!isEditChart && !isProjectBoard ? (
        <MDBox
          display='flex'
          justifyContent='center'
          alignItems='baseline'
          pt={4}
          pb={0.5}
          pr={0}
          direction='column'
          // height='100%' - Removed due to issue in iOS devicess
        >
          <Tooltip title={getCloseMessage(configuratorFlag)} placement='left'>
            {!preventClosing ? (
              <IconButton
                onClick={handleCloseConfigurator}
                sx={{
                  position: 'absolute',
                  top: '26px',
                  left: '10px',
                  zIndex: 1400
                }}
              >
                <FontAwesomeIcon icon='chevron-right' />
              </IconButton>
            ) : (
              <CircularProgress
                color='secondary'
                size={25}
                sx={{
                  position: 'absolute',
                  top: '26px',
                  left: '10px',
                  zIndex: 1400
                }}
              />
            )}
          </Tooltip>
          {children}
        </MDBox>
      ) : (
        <Grid
          container
          direction='column'
          spacing={3}
          // height='100%' - Removed due to issue in iOS devicess
          display='flex'
        >
          <Grid item>
            <Grid
              container
              display='flex'
              alignItems='center'
              flexWrap='nowrap'
              justifyContent='space-between'
              spacing={1}
              sx={{ width: `calc(${width} - 20px)`, overflowX: 'scroll' }}
            >
              <Grid item>
                <Tabs
                  value={configuratorFlag}
                  onChange={handleChange}
                  ref={tabsRef}
                >
                  {isEditChart
                    ? editChartTabs.map((tab: string) => (
                        <Tab
                          key={tab}
                          label={tab}
                          disableTouchRipple
                          sx={{
                            padding: tab === 'Settings' ? '1px' : '5px',
                            marginX: '5px'
                          }}
                          onClick={handleEditChartTabsClick(tab)}
                        ></Tab>
                      ))
                    : projectBoardTabs.map((tab: string) => (
                        <Tab
                          key={tab}
                          label={tab}
                          disableTouchRipple
                          sx={{
                            padding: tab === 'Theme' ? '1px' : '5px',
                            marginX: '5px'
                          }}
                          onClick={handleProjectBoardTabsClick(tab)}
                        ></Tab>
                      ))}
                </Tabs>
              </Grid>
              <Grid item>
                {!preventClosing ? (
                  <IconButton
                    onClick={handleCloseConfigurator}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 10,
                      zIndex: 1400
                    }}
                  >
                    <FaIcon
                      icon='angle-right'
                      typographyProps={{
                        color: 'dark',
                        variant: 'xl',
                        textGradient: 'true'
                      }}
                      style={{ pointerEvents: 'none' }}
                    />
                  </IconButton>
                ) : (
                  <CircularProgress
                    color='secondary'
                    size={20}
                    sx={{
                      zIndex: 1400,
                      marginRight: 1
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item sx={{ marginX: '10px' }}>
            {children}
          </Grid>
        </Grid>
      )}
    </ConfiguratorRoot>
  );
}
export default Configurator;
